import {LandlordTypeOptions} from '@landlords/models';
import type {VatKennzahlen} from '@vat/engine/types';
import {VAT_MAIL_COPY} from '@vat/vat-copy';
import type {NahausContact} from 'app/modules/admin/contacts/models/nahausContact.interface';
import {formatEuro, formatFullEuro} from './vat-format';

/**
 * Steuerberater-Mail (Spec 7): Betreff und Body (Sie-Form) für den Composer – NUR Entwurf.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export interface VatMailDraftParams {
    landlordName: string;
    steuerberaterName: string | null;
    periodLabelText: string;
    version: number;
    kennzahlen: VatKennzahlen;
    attachments: string[];
    dueDateLabel: string | null;
}

export const escapeHtml = (value: string): string =>
    (value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export const buildVatMailSubject = (p: Pick<VatMailDraftParams, 'periodLabelText' | 'landlordName'>): string =>
    VAT_MAIL_COPY.subject(p.periodLabelText, p.landlordName);

const kennzahlenLine = (kz: VatKennzahlen): string =>
    `${VAT_MAIL_COPY.kennzahlen} Kz 81 ${formatFullEuro(kz.kz81)} · Kz 86 ${formatFullEuro(kz.kz86)} · Kz 48 ${formatFullEuro(kz.kz48)} · Kz 66 ${formatEuro(kz.kz66)} · Kz 83 ${formatEuro(kz.kz83)}`;

export const buildVatMailBody = (p: VatMailDraftParams): { html: string; text: string } => {
    const paragraphs = [
        VAT_MAIL_COPY.greeting(p.steuerberaterName),
        VAT_MAIL_COPY.intro(p.periodLabelText, p.landlordName, p.version),
        kennzahlenLine(p.kennzahlen),
        ...(p.dueDateLabel ? [VAT_MAIL_COPY.dueDate(p.dueDateLabel)] : []),
        VAT_MAIL_COPY.attachments(p.attachments),
        VAT_MAIL_COPY.disclaimer,
        VAT_MAIL_COPY.closing,
    ];
    return {
        html: paragraphs.map(paragraph => `<p>${escapeHtml(paragraph)}</p>`).join(''),
        text: paragraphs.join('\n\n'),
    };
};

/** Der Composer befüllt `to` nur aus landlord/tenant/contact/recipientUser (A7) → Steuerberatung als Kontakt. */
export const buildSteuerberaterContact = (steuerberater: { name: string; email: string }, landlordID: string): NahausContact => ({
    id: `vat-steuerberater-${landlordID}`,
    type: LandlordTypeOptions.COMMERCIAL,
    companyName: steuerberater.name,
    emails: [{value: steuerberater.email, label: VAT_MAIL_COPY.contactLabel}],
} as NahausContact);
